import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, HelpCircle, Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { computePackageDetails, reconcileStatedTotal, toEnginePackage } from '@/lib/reconcile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { LineItemRow } from './LineItemRow';
import { ChargeRow, type ChargeFormulaContext } from './ChargeRow';
import { ChargeReviewBanner, type FormulaWarning } from './ChargeReviewBanner';
import { normalizeSection } from '@everplan/shared/lib/section-aliases';
import type { LineItemInput, ChargeInput } from '@everplan/shared';

type LineItemData = Omit<LineItemInput, 'sortOrder'>;
type ChargeData = Omit<ChargeInput, 'sortOrder'>;

interface PackageData {
  name: string;
  description?: string | null;
  isSelected?: boolean;
  lineItems: LineItemData[];
  additionalCharges: ChargeData[];
  statedTotal?: number | null;
}

interface PackageCardProps {
  pkg: PackageData;
  onChange: (updated: PackageData) => void;
  onRemove: () => void;
  showSelection?: boolean;
  warnings?: FormulaWarning[];
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function groupBySection(items: LineItemData[]): Map<string, LineItemData[]> {
  const groups = new Map<string, LineItemData[]>();
  for (const item of items) {
    const key = item.section ?? '';
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return groups;
}

export function PackageCard({ pkg, onChange, onRemove, showSelection, warnings = [] }: PackageCardProps) {
  const [open, setOpen] = useState(true);
  // Lifted from ChargeRow so the review banner can scroll-to + expand a
  // specific row, and so the chevron / badge / banner all share one source
  // of truth for which row's editor is open.
  const [expandedChargeId, setExpandedChargeId] = useState<string | null>(null);
  const chargeRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const toggleChargeExpanded = useCallback((id: string) => {
    setExpandedChargeId((curr) => (curr === id ? null : id));
  }, []);

  const handleReviewClick = useCallback((firstChargeId: string) => {
    setExpandedChargeId(firstChargeId);
    const node = chargeRowRefs.current.get(firstChargeId);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Live recompute: delegate to the shared calc engine so percentage charges
  // resolve against current line items / sibling charges. Synthesized charge
  // ids in the engine are stable per index so cross-charge basis references
  // continue to resolve as the planner edits.
  const computed = computePackageDetails(toEnginePackage(pkg));
  const packageTotal = computed.packageTotal;
  const reconcile = reconcileStatedTotal(pkg.statedTotal ?? null, packageTotal);

  // ---------------------------------------------------------------------
  // Formula context — derived once per render and passed to every ChargeRow.
  // The badge / editor need the package's section list, sibling charges, and
  // resolved per-charge amounts so the editor can show live previews while
  // the planner toggles basis options.
  // ---------------------------------------------------------------------
  const formulaCtx = useMemo(() => {
    const sectionTotals: Record<string, number> = {};
    const sectionsSeen: string[] = [];
    let lineItemTotal = 0;
    for (const li of pkg.lineItems) {
      const qty = li.quantity ?? 1;
      const lineTotal = qty * li.unitCost;
      lineItemTotal += lineTotal;
      const normalized = normalizeSection(li.section ?? null);
      if (normalized) {
        if (!(normalized in sectionTotals)) sectionsSeen.push(normalized);
        sectionTotals[normalized] = (sectionTotals[normalized] ?? 0) + lineTotal;
      }
    }

    return {
      sectionTotals,
      availableSections: sectionsSeen,
      lineItemTotal,
      // Add-ons are not yet modeled per-package in the editor (Phase 5).
      addOnTotal: 0,
      siblingResolvedAmounts: computed.chargesById,
    };
  }, [pkg.lineItems, computed.chargesById]);

  const updateLineItem = (index: number, updated: LineItemData) => {
    const newItems = [...pkg.lineItems];
    newItems[index] = updated;
    onChange({ ...pkg, lineItems: newItems });
  };

  const removeLineItem = (index: number) => {
    onChange({
      ...pkg,
      lineItems: pkg.lineItems.filter((_, i) => i !== index),
    });
  };

  const addLineItem = () => {
    onChange({
      ...pkg,
      lineItems: [
        ...pkg.lineItems,
        { name: '', unitCost: 0, quantity: null, section: null, description: null, notes: null },
      ],
    });
  };

  const updateCharge = (index: number, updated: ChargeData) => {
    const newCharges = [...pkg.additionalCharges];
    newCharges[index] = updated;
    onChange({ ...pkg, additionalCharges: newCharges });
  };

  const removeCharge = (index: number) => {
    onChange({
      ...pkg,
      additionalCharges: pkg.additionalCharges.filter((_, i) => i !== index),
    });
  };

  const addCharge = () => {
    onChange({
      ...pkg,
      additionalCharges: [
        ...pkg.additionalCharges,
        { type: 'other' as const, description: null, amount: 0 },
      ],
    });
  };

  // Build grouped sections for rendering
  const sectionGroups = groupBySection(pkg.lineItems);
  let itemIndex = 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card shadow-ambient">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon-xs" className="shrink-0">
              {open ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          </CollapsibleTrigger>

          <Input
            value={pkg.name}
            onChange={(e) => onChange({ ...pkg, name: e.target.value })}
            placeholder="Package name"
            className="h-8 flex-1 font-semibold"
            onClick={(e) => e.stopPropagation()}
          />

          <span className="shrink-0 text-sm font-semibold text-muted-foreground">
            {formatDollars(packageTotal)}
          </span>

          {showSelection && (
            <Button
              type="button"
              variant={pkg.isSelected ? 'default' : 'outline'}
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onChange({ ...pkg, isSelected: !pkg.isSelected });
              }}
              className="shrink-0 gap-1"
            >
              {pkg.isSelected && <Check className="size-3" />}
              {pkg.isSelected ? 'Selected' : 'Select'}
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onRemove}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>

        {/* Collapsible Content */}
        <CollapsibleContent>
          <div className="space-y-2 px-4 pb-3">
            {/* Package description */}
            <textarea
              value={pkg.description ?? ''}
              onChange={(e) =>
                onChange({ ...pkg, description: e.target.value || null })
              }
              placeholder="Description — e.g. 8 hours coverage, second photographer, online gallery"
              rows={1}
              className="flex w-full resize-none rounded-md border border-input bg-transparent px-3 py-1.5 text-xs shadow-sm placeholder:text-muted-foreground/50 field-sizing-content"
            />

            {reconcile.status === 'mismatch' && (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle>Totals don't match the document</AlertTitle>
                <AlertDescription>
                  Document shows {formatDollars(reconcile.stated!)} but line items + charges sum to{' '}
                  {formatDollars(reconcile.computed)} (
                  {reconcile.diff >= 0 ? '+' : '−'}{formatDollars(Math.abs(reconcile.diff))}).
                  Review the extracted items for missing or duplicated rows.
                </AlertDescription>
              </Alert>
            )}
            {reconcile.status === 'unverified' && (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <HelpCircle className="size-3" />
                No stated total was found on the document to verify against.
              </p>
            )}

            {/* Line Items table */}
            <div>
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    <th className="text-left pb-1 font-medium">Name</th>
                    <th className="text-right pb-1 font-medium w-16">Qty</th>
                    <th className="text-right pb-1 font-medium w-24">Rate</th>
                    <th className="text-right pb-1 font-medium w-20">Total</th>
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {Array.from(sectionGroups.entries()).map(
                    ([section, sectionItems]) => {
                      const startIndex = itemIndex;
                      itemIndex += sectionItems.length;

                      return (
                        <Fragment key={section || '__no_section'}>
                          {section && sectionGroups.size > 1 && (
                            <tr>
                              <td colSpan={5} className="pt-3 pb-0.5">
                                <span className="text-[11px] font-semibold text-muted-foreground/70">
                                  {section}
                                </span>
                              </td>
                            </tr>
                          )}
                          {sectionItems.map((item, i) => (
                            <LineItemRow
                              key={startIndex + i}
                              item={item}
                              onChange={(updated) =>
                                updateLineItem(startIndex + i, updated)
                              }
                              onRemove={() => removeLineItem(startIndex + i)}
                            />
                          ))}
                        </Fragment>
                      );
                    }
                  )}
                </tbody>
              </table>

              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={addLineItem}
                className="mt-0.5 text-muted-foreground"
              >
                <Plus className="size-3" />
                Add Line Item
              </Button>
            </div>

            {/* Additional Charges */}
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Additional Charges
                </h4>
              </div>

              {/* Review banner — renders per-charge formula warnings from the
                  backend guards. chargeIndex maps to the form-charge-N id so
                  clicking scrolls to and expands the right ChargeRow. */}
              <ChargeReviewBanner
                warnings={warnings}
                chargeIndexToId={Object.fromEntries(
                  pkg.additionalCharges.map((_, i) => [i, `form-charge-${i}`])
                )}
                onReviewClick={handleReviewClick}
              />

              {pkg.additionalCharges.map((charge, i) => {
                // For percentage charges, render the engine's live-computed
                // amount instead of the persisted one — that way the planner
                // sees the value update as they edit qty/unitCost. The
                // persisted `amount` is still what gets saved (and the backend
                // recomputes on write per Phase 2.2).
                const isPercentage = charge.calcMethod === 'percentage';
                const selfId = `form-charge-${i}`;
                const resolvedAmount = computed.chargesById[selfId];
                const displayCharge = isPercentage && resolvedAmount != null
                  ? { ...charge, amount: resolvedAmount }
                  : charge;

                const siblings = pkg.additionalCharges.map((sib, sibIdx) => ({
                  id: `form-charge-${sibIdx}`,
                  description: sib.description ?? null,
                  type: sib.type,
                  calcMethod: sib.calcMethod ?? 'flat',
                  basis: (sib.basis as ChargeFormulaContext['siblings'][number]['basis']) ?? null,
                  rateBps: sib.rateBps ?? null,
                }));

                const chargeLabels: Record<string, string> = {};
                for (const sib of siblings) {
                  chargeLabels[sib.id] = sib.description ?? sib.type;
                }

                const rowFormulaContext: ChargeFormulaContext = {
                  selfId,
                  siblings,
                  availableSections: formulaCtx.availableSections,
                  packageContext: {
                    lineItemTotal: formulaCtx.lineItemTotal,
                    addOnTotal: formulaCtx.addOnTotal,
                    sectionTotals: formulaCtx.sectionTotals,
                    siblingResolvedAmounts: formulaCtx.siblingResolvedAmounts,
                  },
                  chargeLabels,
                };

                return (
                  <div
                    key={i}
                    ref={(node) => {
                      if (node) chargeRowRefs.current.set(selfId, node);
                      else chargeRowRefs.current.delete(selfId);
                    }}
                  >
                    <ChargeRow
                      charge={displayCharge}
                      onChange={(updated) => updateCharge(i, updated)}
                      onRemove={() => removeCharge(i)}
                      formulaContext={rowFormulaContext}
                      expanded={expandedChargeId === selfId}
                      onToggleExpanded={() => toggleChargeExpanded(selfId)}
                    />
                  </div>
                );
              })}

              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={addCharge}
                className="mt-0.5 text-muted-foreground"
              >
                <Plus className="size-3" />
                Add Charge
              </Button>
            </div>

            {/* Package Total */}
            <div className="flex items-center justify-end border-t border-border pt-2">
              <span className="text-xs text-muted-foreground mr-2">
                Package Total:
              </span>
              <span className="text-sm font-semibold">
                {formatDollars(packageTotal)}
              </span>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
